/**
 * Builds an `aria-describedby` by combining internal and external ids.
 *
 * It exists because the same normalisation was written six times across
 * distintas —`Select`, `DateInput`, `FileInput`, `Switch`, `RutInput` y
 * `RegionalOfficeSelect` — and two of those copies used `Array.isArray()`,
 * which **does not narrow** a `readonly string[]` inside a union with `string`:
 * TypeScript leaves the type intact in both branches and the value ends up
 * unable to be passed to anything expecting `string`.
 *
 * The underlying problem is the one `form-field.tsx` documents: `FormField`
 * injects `id`, `aria-invalid` and `aria-describedby` into the child through
 * `cloneElement`, and a child that does not declare them loses them **with
 * nothing failing** — the Zod error stays visible but unbound to the control, so
 * a screen reader does not announce it on focus. Every component that accepts
 * them has to combine them with its own, and "combining" is this function.
 *
 * It accepts each part as a string (`"id1 id2"`), an array of ids, or `undefined`.
 * The ORDER matters and is preserved: the internal ids go first — what is
 * selected, what format is expected — and only then the field's description and
 * the error, which is the order a screen reader will read them in.
 *
 * It returns `undefined` (and not `""`) when no id is left: an empty string
 * writes the attribute anyway, and an `aria-describedby=""` is a broken
 * reference, not the absence of one.
 */
export function joinDescribedBy(
  ...parts: readonly (string | readonly string[] | undefined)[]
): string | undefined {
  const ids = parts.flatMap((part) => {
    if (part === undefined) return [];
    // `typeof === 'string'` and not `Array.isArray`: it is the only one of the two
    // that narrows the `string | readonly string[]` union in both branches.
    return typeof part === 'string' ? part.split(' ') : [...part];
  });

  const presentes = ids.filter((id) => id.length > 0);
  return presentes.length > 0 ? presentes.join(' ') : undefined;
}
